import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, MinusIcon, PlusIcon, SlidersHorizontalIcon } from 'lucide-react';
import { ImplementationOption, PricingLayout, promoImplementationFee, usePricing } from '../../contexts/PricingContext';
import { ControlNumberField } from './ControlNumberField';
import { AssumptionControls } from './AssumptionControls';
const AD_SPEND_STEP = 2500;
const formatUsd = (value: number) => value.toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});
const sectionLabel = 'text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500';
const IMPLEMENTATION_OPTIONS: {
  key: ImplementationOption;
  label: string;
}[] = [{
  key: 'full',
  label: 'In full'
}, {
  key: 'split',
  label: 'Split ×3'
}, {
  key: 'promo',
  label: '$3K promo'
}];
interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}
function ToggleRow({
  label,
  checked,
  onChange
}: ToggleRowProps) {
  return <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="w-full flex items-center justify-between gap-3 py-1.5 text-left text-[13px] font-medium text-slate-300 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] rounded" data-id="element-1273" data-mp="a0s4axbrzens">
      <span className="whitespace-nowrap" data-id="element-1274" data-mp="3n9wmyzzz4iqf">{label}</span>
      <span aria-hidden="true" className={`relative inline-flex h-[18px] w-8 shrink-0 rounded-full transition-colors duration-150 ease-out ${checked ? 'bg-[#14684d]' : 'bg-slate-700'}`} data-id="element-1275" data-mp="3i9s3d13rr7vz">
        <span className={`absolute top-[3px] left-[3px] h-3 w-3 rounded-full bg-white transition-transform duration-150 ease-out ${checked ? 'translate-x-[14px]' : ''}`} data-id="element-1276" data-mp="3raq6lh6dvbdi" />
      </span>
    </button>;
}

/**
 * Quiet left-side panel for tailoring the proposal. Every change updates the
 * shared deck settings, so the Partnership page and the PDF stay in step.
 */
interface ProposalControlsProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}
export function ProposalControls({
  open,
  onOpenChange
}: ProposalControlsProps) {
  const {
    moneyBackGuarantee,
    setMoneyBackGuarantee,
    guaranteeRevealed,
    setGuaranteeRevealed,
    hasRecruiter,
    setHasRecruiter,
    hasLeader,
    setHasLeader,
    goToMarket,
    setGoToMarket,
    adSpend,
    setAdSpend,
    cpaLow,
    setCpaLow,
    cpaHigh,
    setCpaHigh,
    hiresLow,
    hiresHigh,
    pricingModel,
    setPricingModel,
    pricingLayout,
    setPricingLayout,
    setImplementationOption,
    isPromo,
    splitBuildFee,
    promoDeadline,
    setPromoDeadline,
    regularImplementationFee,
    hasImplementationFee,
    setHasImplementationFee
  } = usePricing();
  const stepAdSpend = (direction: 1 | -1) => setAdSpend(Math.max(AD_SPEND_STEP, adSpend + direction * AD_SPEND_STEP));
  if (!open) {
    return <button type="button" onClick={() => onOpenChange(true)} aria-expanded={false} aria-label="Open proposal settings" className="no-print fixed left-4 top-20 z-[110] hidden lg:flex flex-col items-center gap-2 rounded-xl border border-slate-800 bg-[#0d141f]/95 px-2 py-3 text-slate-400 shadow-lg hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1277" data-mp="2vc9cm0872c3r">
        <SlidersHorizontalIcon className="w-4 h-4" aria-hidden="true" data-id="element-1278" data-mp="27n7ypxgle8r3" />
        <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] font-bold uppercase tracking-[0.14em]" data-id="element-1279" data-mp="27lwtdt7i0sxv">
          Tailor
        </span>
      </button>;
  }
  const promoActive = isPromo;
  const splitActive = splitBuildFee;
  const promoPrice = formatUsd(promoImplementationFee(hasRecruiter));
  const implementationHint = promoActive && splitActive ? `${promoPrice} (regularly ${formatUsd(regularImplementationFee)}), split: a third at signing, then Months 2 and 3, if they sign by:` : splitActive ? 'A third at signing, then Months 2 and 3.' : promoActive ? `${promoPrice} one time (regularly ${formatUsd(regularImplementationFee)}), if they sign by:` : `${formatUsd(regularImplementationFee)} one time, at signing.`;
  return <motion.aside aria-label="Proposal settings" initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} onWheel={e => e.stopPropagation()} className="no-print fixed left-4 top-20 z-[110] hidden lg:block w-60 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain rounded-xl border border-slate-800 bg-[#0d141f]/95 px-4 py-4 shadow-lg" data-id="element-1280" data-mp="3jyaivj3yox9g">
      <div className="flex items-center justify-between" data-id="element-1281" data-mp="uc4fias5cv35">
        <div className={sectionLabel} data-id="element-1282" data-mp="za5x0r9xcmci">Tailor proposal</div>
        <button type="button" onClick={() => onOpenChange(false)} aria-label="Close proposal settings" className="-mr-1.5 grid place-items-center h-6 w-6 rounded text-slate-500 hover:text-white transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1283" data-mp="z9nrt55aaxsg">
          <ChevronLeftIcon className="w-4 h-4" data-id="element-1284" data-mp="1dfy41ulxewqm" />
        </button>
      </div>
      <div className="mt-2" data-id="element-1285" data-mp="1ddv3ign5dl7n">
        <ToggleRow label="Corporate recruiter" checked={hasRecruiter} onChange={setHasRecruiter} data-id="element-1286" data-mp="1s42ob30mngbi" />
        <ToggleRow label="Find a leader" checked={hasLeader} onChange={setHasLeader} data-id="element-1287" data-mp="1n4zj9iknda3j" />
        <ToggleRow label="Go to market" checked={goToMarket && hasLeader} onChange={next => {
        setGoToMarket(next);
        // A go-to-market proposal starts with the leader search.
        if (next) setHasLeader(true);
      }} data-id="element-1288" data-mp="21366m4ryzroy" />
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800" data-id="element-1289" data-mp="2s0mlaa84tzx">
        <div className={sectionLabel} data-id="element-1290" data-mp="21vpht33fboaz">Pricing</div>
        <div className="mt-1.5" data-id="element-1291" data-mp="26tilnph98683">
          <ToggleRow label="Implementation fee" checked={hasImplementationFee} onChange={setHasImplementationFee} data-id="element-1292" data-mp="26uk1065azevm" />
        </div>
        {hasImplementationFee && <div className="mt-1.5" data-id="element-1293" data-mp="2gsg90p2y1tpi">
            <div role="group" aria-label="How the implementation fee is paid" className="grid grid-cols-3 gap-1" data-id="element-1294" data-mp="2gpbyzb2ss3qx">
              {IMPLEMENTATION_OPTIONS.map(option => {
            // Split and promo combine; "In full" means neither.
            const active = option.key === 'full' ? !promoActive && !splitActive : option.key === 'split' ? splitActive : promoActive;
            return <button key={option.key} type="button" aria-pressed={active} onClick={() => setImplementationOption(option.key)} className={`rounded-md border px-1 py-1 text-[11.5px] font-semibold whitespace-nowrap transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${active ? 'border-[#14684d] bg-[#14684d] text-white' : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`} data-id="element-1295" data-mp="38jntnm9x1vjb">
                    {option.key === 'promo' ? `${promoImplementationFee(hasRecruiter) / 1000}K promo` : option.label}
                  </button>;
          })}
            </div>
            <p className="mt-1.5 text-[11.5px] leading-snug text-slate-500" data-id="element-1296" data-mp="1zbkh7zdxjlbq">
              {implementationHint}
            </p>
            {promoActive && <input type="date" aria-label="Promo deadline" value={promoDeadline} onChange={e => e.target.value && setPromoDeadline(e.target.value)} className="mt-1 w-full rounded-md border border-slate-700 bg-[#0d141f] px-2 py-1.5 text-[13px] text-slate-200 [color-scheme:dark] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1297" data-mp="12v6kicq3k4ec" />}
          </div>}
        <div className="mt-2" data-id="element-1298" data-mp="1ql0ridnqiyqo">
          <ToggleRow label="$1K day-45 bonus" checked={pricingModel === 'flagship'} onChange={next => setPricingModel(next ? 'flagship' : 'monthly')} data-id="element-1299" data-mp="1qk7va6mejrms" />
          <p className="text-[11.5px] leading-snug text-slate-500" data-id="element-1300" data-mp="1hjjcyzpz7543">
            {pricingModel === 'flagship' ? 'Flagship: $1,000 per rep still active at day 45.' : 'Off: only the $500 day-15 placement bonus applies.'}
          </p>
        </div>
        <label className="mt-2 block" data-id="element-1301" data-mp="3tt9u1iiha4bb">
          <span className="text-[12px] text-slate-400" data-id="element-1302" data-mp="3ts85cb8jhjb4">Page 2 layout</span>
          <select value={pricingLayout} onChange={e => setPricingLayout(e.target.value as PricingLayout)} className="mt-1 w-full rounded-md border border-slate-700 bg-[#0d141f] px-2 py-1.5 text-[13px] text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1303" data-mp="3pszg9jog29lw">
            <option value="row" data-id="element-1304" data-mp="3b3l2lzdu560p">A · Four-figure row</option>
            <option value="tiers" data-id="element-1305" data-mp="36504q6m22yff">Tier table</option>
            <option value="twoStep" data-id="element-1306" data-mp="362du1y3vc2sz">B · To start, then monthly</option>
            <option value="feeList" data-id="element-1307" data-mp="3kupg0unv4itf">C · Fee list + hires</option>
            <option value="tiersStrip" data-id="element-1308" data-mp="3g24qi5bzorvp">D · Tier table + fee strip</option>
          </select>
        </label>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800" data-id="element-1309" data-mp="2sbiujcf9ey22">
        <div className={sectionLabel} data-id="element-1310" data-mp="2ne0i3c21a0pb">Guarantees</div>
        <div className="mt-1.5" data-id="element-1311" data-mp="2nd7iqw5emdh7">
          <ToggleRow label="Money-back" checked={moneyBackGuarantee} onChange={setMoneyBackGuarantee} data-id="element-1312" data-mp="20yehjb8cy92n" />
          <ToggleRow label="Performance" checked={guaranteeRevealed} onChange={setGuaranteeRevealed} data-id="element-1313" data-mp="1w0mrynv6qz1m" />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-800" data-id="element-1314" data-mp="1m1njb5af73em">
        <div className={sectionLabel} data-id="element-1315" data-mp="1m3i2of61mngl">Monthly ad spend</div>
        <div className="mt-2 flex items-center gap-1.5" data-id="element-1316" data-mp="1i0bzxhi53eev">
          <button type="button" onClick={() => stepAdSpend(-1)} disabled={adSpend <= AD_SPEND_STEP} aria-label="Decrease ad spend by $2,500" className="grid place-items-center h-8 w-8 shrink-0 rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 disabled:opacity-40 disabled:hover:text-slate-400 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1317" data-mp="1d93pqj8zjp0n">
            <MinusIcon className="w-3.5 h-3.5" data-id="element-1318" data-mp="35xumr9tcnhfz" />
          </button>
          <ControlNumberField label="Monthly ad spend" value={adSpend} min={AD_SPEND_STEP} onCommit={setAdSpend} className="flex-1" data-id="element-1319" data-mp="35zns8ls87vme" />
          <button type="button" onClick={() => stepAdSpend(1)} aria-label="Increase ad spend by $2,500" className="grid place-items-center h-8 w-8 shrink-0 rounded-md border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1320" data-mp="3jx2nw892ozmt">
            <PlusIcon className="w-3.5 h-3.5" data-id="element-1321" data-mp="30xyjxdwe1w20" />
          </button>
        </div>
      </div>

      <div className="mt-4" data-id="element-1322" data-mp="23j44l2ycytyt">
        <div className={sectionLabel} data-id="element-1323" data-mp="23ht1l5cqlq8r">Ad cost per hire</div>
        <div className="mt-2 flex items-center gap-1.5" data-id="element-1324" data-mp="28hxkf214ax25">
          <ControlNumberField label="Cost per hire, low" value={cpaLow} min={1} onCommit={next => {
          setCpaLow(next);
          if (next > cpaHigh) setCpaHigh(next);
        }} className="flex-1" data-id="element-1325" data-mp="28gcob7z5n626" />
          <span className="text-slate-600 text-[12px]" aria-hidden="true" data-id="element-1326" data-mp="2a5pwqxi1pq43">–</span>
          <ControlNumberField label="Cost per hire, high" value={cpaHigh} min={1} onCommit={next => setCpaHigh(Math.max(cpaLow, next))} className="flex-1" data-id="element-1327" data-mp="257ofmi46k0v9" />
        </div>
      </div>

      <p className="mt-3 text-[12px] text-slate-500" data-id="element-1328" data-mp="1diuy8h21khk3">
        Projects{' '}
        <span className="font-semibold text-slate-300" data-id="element-1329" data-mp="1ri4fzl5wbjq6">
          {hiresLow}–{hiresHigh} hires
        </span>{' '}
        / month
      </p>

      <AssumptionControls data-id="element-1330" data-mp="32vq73bvbfvec" />
    </motion.aside>;
}